<template>
  <div class="flex flex-col w-full gap-4 h-full overflow-y-auto px-6 pb-6">
    <!-- Example banner -->
    <div class="p-3 rounded-lg bg-primary-50 text-primary-700 text-sm flex items-center gap-2">
      <i class="pi pi-info-circle" />
      <span>זהו תור לדוגמה שמראה לכם איך זה עובד. הוא לא מופיע ללקוחות ולא נשמר.</span>
    </div>

    <!-- Client header (mirrors a real appointment) -->
    <div class="flex w-full flex-col gap-2 border-b pb-4">
      <div class="text-sm flex justify-between items-center">
        <div class="flex flex-col gap-1 pe-4">
          <span class="text-xs text-gray-500">לקוח</span>
          <span class="font-medium">{{ clientName }}</span>
        </div>
        <div class="flex gap-2 items-center">
          <span class="rounded-full px-4 py-1.5 border border-gray-300 flex-none text-sm">דף לקוח</span>
          <span class="rounded-full flex items-center justify-center p-2 border border-green-500 bg-green-50">
            <i class="pi pi-whatsapp text-2xl" />
          </span>
          <span class="rounded-full flex items-center justify-center p-2 border border-blue-500 bg-blue-50">
            <i class="pi pi-phone text-2xl" />
          </span>
        </div>
      </div>
    </div>

    <!-- Service -->
    <div class="flex flex-col w-full gap-2">
      <span class="text-xs text-gray-500">שירות</span>
      <div class="flex gap-3 items-center p-3 rounded-xl border">
        <div class="w-12 h-12 rounded-lg flex-none" :style="{ background: color }" />
        <div class="flex flex-col">
          <span class="font-medium text-sm">{{ serviceName }}</span>
          <span class="text-xs text-gray-500">{{ durationLabel }} · ₪120</span>
        </div>
      </div>
    </div>

    <!-- Times -->
    <div class="w-full grid grid-cols-2 border-t border-b py-4">
      <div class="flex flex-col gap-1 pe-4">
        <span class="text-xs text-gray-500">שעת התחלה</span>
        <span class="text-sm">{{ startLabel }}</span>
      </div>
      <div class="flex flex-col gap-1 pe-4">
        <span class="text-xs text-gray-500">שעת סיום (משוער)</span>
        <span class="text-sm">{{ endLabel }}</span>
      </div>
    </div>

    <div class="flex justify-between items-center">
      <span>סה״כ לתשלום</span>
      <span>₪120</span>
    </div>

    <!-- Illustrative actions (what a real appointment offers) -->
    <div class="w-full flex flex-col mt-2">
      <span class="font-medium mb-3">מה אפשר לעשות עם תור אמיתי</span>
      <div class="flex flex-col gap-2">
        <div
          v-for="action in actions"
          :key="action.label"
          class="flex items-center gap-3 p-3 rounded-xl border bg-white"
        >
          <i :class="['pi', action.icon, 'text-primary-600']" />
          <div class="flex flex-col">
            <span class="text-sm font-medium">{{ action.label }}</span>
            <span class="text-xs text-gray-500">{{ action.sub }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { DateTime } from 'luxon';
import type { ICalendarEvent } from '@torim-app/types';

const props = defineProps<{ event: ICalendarEvent }>();

const rawTitle = computed(() => (props.event?.title ?? '').replace('(לדוגמה)', '').trim());
const serviceName = computed(() => rawTitle.value.split('·')[0]?.trim() || 'שירות');
const clientName = computed(() => rawTitle.value.split('·')[1]?.trim() || 'דנה לוי');
const color = computed(() => props.event?.serviceColor ?? '#8864ee');

// vue-cal hands us Date objects on click; our demo events store strings — handle both.
const toDateTime = (value: unknown): DateTime => {
  if (value instanceof Date) return DateTime.fromJSDate(value);
  return DateTime.fromFormat(String(value ?? ''), 'yyyy-MM-dd HH:mm');
};
const startDt = computed(() => toDateTime(props.event?.start));
const endDt = computed(() => toDateTime(props.event?.end));
const startLabel = computed(() => startDt.value.isValid ? startDt.value.toFormat('HH:mm') : '09:30');
const endLabel = computed(() => endDt.value.isValid ? endDt.value.toFormat('HH:mm') : '10:15');
const durationLabel = computed(() => {
  const mins = endDt.value.isValid && startDt.value.isValid ? endDt.value.diff(startDt.value, 'minutes').minutes : 45;
  return `${Math.round(mins)} דקות`;
});

const actions = [
  { icon: 'pi-pencil', label: 'עריכת הביקור', sub: 'שינוי שעה, אורך התור ושירותים' },
  { icon: 'pi-money-bill', label: 'בקשת תשלום', sub: 'שליחת קישור לתשלום ללקוח' },
  { icon: 'pi-whatsapp', label: 'הודעה ללקוח', sub: 'תזכורת או הודעה אישית' },
  { icon: 'pi-check-circle', label: 'סיום תור', sub: 'סימון שהתור בוצע' },
  { icon: 'pi-trash', label: 'ביטול תור', sub: 'הלקוח יקבל הודעת עדכון' },
];
</script>
